import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { encrypt, decrypt } from '../xor';
import { FaClock, FaMapMarkerAlt } from 'react-icons/fa';

const formatTime = (timeInSeconds) => {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${hours} h. ${minutes} min. ${seconds} sec.`;
};

const randomizeObjectKeys = (obj) => {
  const keys = Object.keys(obj);
  for (let i = keys.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [keys[i], keys[j]] = [keys[j], keys[i]];
  }
  const newObj = {};
  keys.forEach(key => {
    newObj[key] = obj[key];
  });
  return newObj;
};

const CalendarHistory = ({ historyData, month, year, onSelectDate }) => {
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const pad = (num) => num.toString().padStart(2, '0');
  const getDateString = (day) => `${year}-${pad(month + 1)}-${pad(day)}`;

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '5px',
        marginBottom: '10px',
      }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ fontWeight: 'bold', textAlign: 'center' }}>{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx}/>;
          const dateStr = getDateString(day);
          const record = historyData.find(item => item.date === dateStr);
          const bgColor = record
            ? (record.worked > 0 ? '#27ae60' : '#e74c3c')
            : '#ccc';
          return (
            <div
              key={idx}
              onClick={() => onSelectDate(record || { date: dateStr, worked: 0 })}
              style={{
                backgroundColor: bgColor,
                color: '#fff',
                borderRadius: '4px',
                padding: '8px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ClockIc = () => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [error, setError] = useState(null);
  const [lastEvent, setLastEvent] = useState(localStorage.getItem('lastEvent') || null);
  const [baseTime, setBaseTime] = useState(0);
  const [displayTime, setDisplayTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyMonth, setHistoryMonth] = useState(new Date().getMonth());
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const encryptedChatIdFromUrl = params.get('auth_token');
  const [chatId, setChatId] = useState(null);

  useEffect(() => {
    if (!encryptedChatIdFromUrl) return;
    const payload = decrypt(encryptedChatIdFromUrl);
    if (payload) setChatId(payload.split('|', 1)[0]);
  }, [encryptedChatIdFromUrl]);

  useEffect(() => {
    localStorage.setItem('lastEvent', lastEvent);
  }, [lastEvent]);

  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setAccuracy(pos.coords.accuracy);
      },
      err => setError(err.message),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    const fetchLast = async () => {
      if (!encryptedChatIdFromUrl) return;
      const res = await fetch(`http://127.0.0.1:5000/auth?auth_token=${encryptedChatIdFromUrl}`);
      if (!res.ok) return;
      const data = await res.json();
      const ev = data.last_event;
      setLastEvent(ev);
      setBaseTime(data.total_worked_today);
      if (ev === 'clock_in') {
        setTimerRunning(true);
        setStartTimestamp(Date.now());
      } else {
        setTimerRunning(false);
        setDisplayTime(data.total_worked_today);
      }
    };
    fetchLast();
    const iv = setInterval(fetchLast, 30000);
    return () => clearInterval(iv);
  }, [encryptedChatIdFromUrl]);

  useEffect(() => {
    if (timerRunning) {
      const iv = setInterval(() => {
        const diff = (Date.now() - startTimestamp) / 1000;
        setDisplayTime(baseTime + diff);
      }, 1000);
      return () => clearInterval(iv);
    } else {
      setDisplayTime(baseTime);
    }
  }, [timerRunning, baseTime, startTimestamp]);

  const handleClockEvent = async () => {
    if (!chatId) return;
    const eventType = lastEvent === 'clock_in' ? 'clock_out' : 'clock_in';
    const clockData = {
      event_type: eventType,
      latitude,
      longitude,
      auth_token: chatId,
    };
    const payload = encrypt(JSON.stringify(randomizeObjectKeys(clockData)));
    try {
      const res = await fetch('http://127.0.0.1:5000/clock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clock_json: payload }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('Clock error', data);
        return;
      }
      setBaseTime(data.total_worked_today);
      if (lastEvent === 'clock_in') {
        setLastEvent('clock_out');
        setTimerRunning(false);
      } else {
        setLastEvent('clock_in');
        setTimerRunning(true);
        setStartTimestamp(Date.now());
      }
    } catch (e) {
      console.error('Clock exception', e);
    }
  };

  const fetchHistory = async () => {
    if (!chatId) return;
    const cipher = encrypt(chatId);
    const res = await fetch(`http://127.0.0.1:5000/history?chat_id=${cipher}`);
    if (!res.ok) return;
    const data = await res.json();
    setHistoryData(data.history);
  };

  const handleViewHistory = () => {
    fetchHistory();
    setShowHistoryModal(true);
  };
  const handlePrevMonth = () => {
    if (historyMonth === 0) {
      setHistoryMonth(11);
      setHistoryYear(historyYear - 1);
    } else {
      setHistoryMonth(historyMonth - 1);
    }
  };
  const handleNextMonth = () => {
    if (historyMonth === 11) {
      setHistoryMonth(0);
      setHistoryYear(historyYear + 1);
    } else {
      setHistoryMonth(historyMonth + 1);
    }
  };
  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedHistory(null);
  };

  const buttonText = lastEvent === 'clock_in' ? 'Clock Out' : 'Clock In';
  const buttonColor = lastEvent === 'clock_in' ? '#e74c3c' : '#27ae60';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e4f2ff, #5563DE)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '"Roboto", sans-serif'
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
        padding: '30px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
      }}>
        <h1 style={{ marginBottom: '20px', color: '#333' }}>Clock In/Out</h1>
        <p style={{ marginBottom: '10px', fontSize: '0.9em', color: '#555' }}>
          Chat ID: <strong>{chatId || 'N/A'}</strong>
        </p>
        {error && <p style={{ color: '#e74c3c' }}>Error: {error}</p>}
        {latitude != null && longitude != null ? (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '5px 0', fontSize: '1em', color: '#555' }}>
              <FaMapMarkerAlt style={{ marginRight: '8px', color: '#5563DE' }}/>
              Latitude: <strong>{latitude}</strong>
            </p>
            <p style={{ margin: '5px 0', fontSize: '1em', color: '#555' }}>
              <FaMapMarkerAlt style={{ marginRight: '8px', color: '#5563DE' }}/>
              Longitude: <strong>{longitude}</strong>
            </p>
            <p style={{ margin: '5px 0', fontSize: '0.9em', color: '#888' }}>
              Accuracy: {accuracy ? `${accuracy} meters` : 'N/A'}
            </p>
          </div>
        ) : (
          <p style={{ color: '#555' }}>Getting location...</p>
        )}
        <button
          onClick={handleClockEvent}
          style={{
            backgroundColor: buttonColor,
            color: '#fff',
            padding: '12px 25px',
            fontSize: '1em',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '100%',
            marginBottom: '15px',
          }}
        >
          {buttonText}
        </button>
        <div style={{
          background: 'linear-gradient(135deg, #333, #000)',
          borderRadius: '8px',
          padding: '15px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 10px rgba(0,255,0,0.7)',
          color: '#0f0',
          fontFamily: 'monospace',
          fontSize: '1.5em',
          letterSpacing: '2px',
          marginTop: '15px',
        }}>
          <FaClock style={{ marginRight: '10px' }}/>
          <span>{formatTime(displayTime)}</span>
        </div>
        <button
          onClick={handleViewHistory}
          style={{
            backgroundColor: '#5563DE',
            color: '#fff',
            padding: '10px 20px',
            fontSize: '1em',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '20px',
            width: '100%',
          }}
        >
          View History
        </button>
      </div>

      {showHistoryModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            textAlign: 'center',
          }}>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>History</h2>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '10px',
            }}>
              <button onClick={handlePrevMonth} style={{
                backgroundColor: '#5563DE', color: '#fff', border: 'none',
                borderRadius: '4px', padding: '5px 10px', cursor: 'pointer',
              }}>Prev</button>
              <span style={{ fontWeight: 'bold' }}>
                {new Date(historyYear, historyMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={handleNextMonth} style={{
                backgroundColor: '#5563DE', color: '#fff', border: 'none',
                borderRadius: '4px', padding: '5px 10px', cursor: 'pointer',
              }}>Next</button>
            </div>
            <CalendarHistory
              historyData={historyData}
              month={historyMonth}
              year={historyYear}
              onSelectDate={setSelectedHistory}
            />
            {selectedHistory && (
              <div style={{
                backgroundColor: '#f0f0f0', padding: '15px',
                borderRadius: '8px', marginBottom: '15px',
              }}>
                <h3>Date: {selectedHistory.date}</h3>
                <p>Worked: {formatTime(selectedHistory.worked)}</p>
              </div>
            )}
            <button onClick={closeHistoryModal} style={{
              backgroundColor: '#5563DE', color: '#fff',
              padding: '10px 20px', border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontSize: '1em',
            }}>Close History</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClockIc;
